<template>
  <div class="post-answer-view">
    <el-card class="answer-container" shadow="never" @click="handleAnswerClick">
      <div class="answer-content">
        <template v-if="!answer.content_type || answer.content_type === 'text'">
          <div class="text-content" v-html="renderContent()"></div>
        </template>
        <template v-else-if="answer.content_type === 'quote'">
          <figure class="quote-content">
            <div class="quote-mark" aria-hidden="true">&ldquo;</div>
            <blockquote v-html="renderQuoteContent()"></blockquote>
          </figure>
        </template>
        <template v-else-if="isKeyTermsCard(answer)">
          <KeyTermsCard :card="answer" compact />
        </template>
        <template v-else-if="answer.content_type === 'image'">
          <div class="image-content">
            <img v-if="answer.fileList && answer.fileList.length > 0"
              :src="answer.fileList[0].url"
              :alt="'Answer Image'"
            >
          </div>
        </template>
        <template v-else-if="answer.content_type === 'video'">
          <div class="video-content">
            <video v-if="answer.fileList && answer.fileList.length > 0" controls>
              <source :src="answer.fileList[0].url" :type="answer.fileList[0].mime_type">
              Your browser does not support the video tag.
            </video>
          </div>
        </template>
        <template v-else-if="answer.content_type === 'quiz'">
          <div class="quiz-content">
            <div class="quiz-question"><strong>Question:</strong> <span>{{ answer.quiz_content.question }}</span></div>
            <div v-for="(option, idx) in answer.quiz_content.options.filter(option => option !== '')" :key="idx"
                 :class="['quiz-option', { 'correct-answer': isCorrectAnswer(idx) }]">
              <div class="quiz-option-content">
                <div style="display: flex; flex: 1; align-items: flex-start;">
                  <span style="min-width: 30px; line-height: 1.5;">{{ ['A','B','C','D'][idx] }}.</span>
                  <span style="line-height: 1.5;">{{ option }}</span>
                </div>
                <i v-if="isCorrectAnswer(idx)" class="el-icon-check correct-icon"></i>
              </div>
            </div>
            <div v-if="showAnswer" class="quiz-explanation">
              <div class="explanation-header">Explanation:</div>
              <div class="explanation-content">{{ answer.quiz_content.explanation }}</div>
            </div>
          </div>
        </template>
      </div>

      <div class="answer-footer">
        <div class="footer-left">
          <span v-if="index !== null" class="card-index">Card #{{ index + 1 }}</span>
          <span v-if="answer.created_at" class="answer-date">{{ formatDate(answer.created_at) }}</span>
        </div>
      </div>
      
      <!-- Floating Action Menu -->
      <div class="floating-actions">
        <div class="action-menu">
          <button
            class="action-item"
            @click.stop="handleEdit"
          >
            Edit
          </button>
          <button
            v-if="answer.content_type === 'quiz'"
            class="action-item"
            @click.stop="handleFixByAI"
          >
            Fix by AI
          </button>
          <button
            class="action-item delete-button"
            @click.stop="handleDelete"
          >
            Delete
          </button>
        </div>
      </div>
    </el-card>

    <!-- AI Fix Comparison Dialog -->
    <el-dialog
      title="AI Fix Comparison"
      :visible.sync="showComparisonDialog"
      width="80%"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      :append-to-body="true"
    >
      <div class="comparison-container">
        <div class="comparison-column">
          <h3>Original Card</h3>
          <div class="card-preview">
            <template v-if="originalAnswerData && originalAnswerData.content_type === 'quiz' && originalAnswerData.quiz_content">
              <div class="quiz-display">
                <div class="quiz-question-display">
                  <strong>Question:</strong> {{ originalAnswerData.quiz_content.question }}
                </div>
                <div class="quiz-options-display">
                  <div v-for="(option, idx) in originalAnswerData.quiz_content.options.filter(opt => opt !== '')" :key="idx"
                       :class="['quiz-option-display', { 'correct': originalAnswerData.quiz_content.correct_answer === ['A','B','C','D'][idx] }]">
                    <span>{{ ['A','B','C','D'][idx] }}. {{ option }}</span>
                    <i v-if="originalAnswerData.quiz_content.correct_answer === ['A','B','C','D'][idx]" class="el-icon-check"></i>
                  </div>
                </div>
                <div class="quiz-explanation-display" v-if="originalAnswerData.quiz_content.explanation">
                  <strong>Explanation:</strong> {{ originalAnswerData.quiz_content.explanation }}
                </div>
              </div>
            </template>
            <template v-else-if="originalAnswerData">
              <div class="text-content">{{ originalAnswerData.content }}</div>
            </template>
          </div>
        </div>

        <div class="comparison-column">
          <h3>AI Suggestion</h3>
          <div class="card-preview">
            <template v-if="isLoadingAI">
              <div class="loading-state">
                <i class="el-icon-loading" style="font-size: 32px; color: #409EFF;"></i>
                <p>AI is analyzing the card...</p>
              </div>
            </template>
            <template v-else-if="aiResponse">
              <template v-if="aiResponse.trim().toLowerCase() === 'true'">
                <div class="no-defects-state">
                  <i class="el-icon-circle-check" style="font-size: 48px; color: #67c23a;"></i>
                  <p style="font-size: 16px; font-weight: 500; margin-top: 16px;">No defects found!</p>
                  <p style="color: #909399;">This card is already correct and doesn't need any fixes.</p>
                </div>
              </template>
              <template v-else>
                <div class="quiz-display" v-if="parsedAIResponse">
                  <div class="quiz-question-display">
                    <strong>Question:</strong> {{ parsedAIResponse.quiz_content.question }}
                  </div>
                  <div class="quiz-options-display">
                    <div v-for="(option, idx) in parsedAIResponse.quiz_content.options.filter(opt => opt !== '')" :key="idx"
                         :class="['quiz-option-display', { 'correct': parsedAIResponse.quiz_content.correct_answer === ['A','B','C','D'][idx] }]">
                      <span>{{ ['A','B','C','D'][idx] }}. {{ option }}</span>
                      <i v-if="parsedAIResponse.quiz_content.correct_answer === ['A','B','C','D'][idx]" class="el-icon-check"></i>
                    </div>
                  </div>
                  <div class="quiz-explanation-display" v-if="parsedAIResponse.quiz_content.explanation">
                    <strong>Explanation:</strong> {{ parsedAIResponse.quiz_content.explanation }}
                  </div>
                </div>
                <div v-else class="error-state">
                  <i class="el-icon-warning" style="font-size: 32px; color: #f56c6c;"></i>
                  <p>Failed to parse AI response</p>
                </div>
              </template>
            </template>
          </div>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="cancelAIFix" v-if="!aiResponse || aiResponse.trim().toLowerCase() !== 'true'">Cancel</el-button>
        <el-button
          type="primary"
          @click="confirmAIFix"
          :disabled="isLoadingAI || !aiResponse"
        >
          {{ aiResponse && aiResponse.trim().toLowerCase() === 'true' ? 'OK' : 'Apply AI Fixes' }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { postRequest } from '@/apis/front'
import { getUserId } from '@/utils/store'
import KeyTermsCard from './KeyTermsCard.vue'

export default {
  name: 'PostAnswerView',
  components: {
    KeyTermsCard
  },
  props: {
    answer: {
      type: Object,
      required: true,
      default: () => ({
        content: '',
        content_type: 'text',
        created_at: Date.now(),
        fileList: [],
        quiz_content: {
          question: '',
          options: [],
          explanation: ''
        },
        is_hidden: false
      })
    },
    index: {
      type: Number,
      default: null
    },
    showAnswer: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      showComparisonDialog: false,
      aiResponse: null,
      isLoadingAI: false,
      originalAnswerData: null
    }
  },
  computed: {
    parsedAIResponse() {
      if (!this.aiResponse || this.aiResponse.trim().toLowerCase() === 'true') {
        return null
      }
      try {
        return JSON.parse(this.aiResponse)
      } catch (error) {
        return null
      }
    }
  },
  methods: {
    isKeyTermsCard(card) {
      const contentType = (card && card.content_type ? card.content_type : '').toString().toLowerCase()
      return ['key_terms', 'keyterms', 'key-terms', 'key_terms_card', 'keyterms_card', 'key-terms-card', 'terms', 'term_list'].includes(contentType)
    },
    getQuoteText() {
      return (
        this.answer.content ||
        this.answer.quote_content ||
        this.answer.quote ||
        this.answer.text ||
        ''
      ).trim()
    },
    renderQuoteContent() {
      const quote = this.getQuoteText()
      if (!quote) {
        return '<p class="quote-placeholder">No quote is available for this card yet.</p>'
      }

      marked.setOptions({
        breaks: true,
        gfm: true,
        headerIds: false,
        mangle: false
      })
      return DOMPurify.sanitize(marked(quote), {
        ADD_TAGS: ['mark'],
        ADD_ATTR: ['target', 'rel']
      })
    },
    renderContent() {
      if (!this.answer.content) return ''

      // Check the editor type
      const editorType = this.answer.editor_type || 'auto'

      // If editor type is explicitly set, use it
      if (editorType === 'markdown') {
        // Configure marked for better rendering
        marked.setOptions({
          breaks: true, // Enable line breaks
          gfm: true, // GitHub Flavored Markdown
          headerIds: false,
          mangle: false
        })
        const rawHtml = marked(this.answer.content)
        return DOMPurify.sanitize(rawHtml, {
          ADD_TAGS: ['mark'], // Allow highlight tags
          ADD_ATTR: ['target', 'rel'] // Allow link attributes
        })
      } else if (editorType === 'richtext') {
        // Rich text is already HTML, just sanitize it
        return DOMPurify.sanitize(this.answer.content)
      } else {
        // Auto-detect content type
        const content = this.answer.content
        const hasHtmlTags = this.detectHtmlTags(content)
        const hasMarkdownPatterns = this.detectMarkdownPatterns(content)

        // If HTML tags detected, treat as rich text
        if (hasHtmlTags) {
          return DOMPurify.sanitize(content)
        }

        // If markdown patterns detected, render as markdown
        if (hasMarkdownPatterns || editorType === 'auto') {
          // Try to render as markdown
          try {
            marked.setOptions({
              breaks: true,
              gfm: true,
              headerIds: false,
              mangle: false
            })
            const rawHtml = marked(content)
            // Only use markdown if it actually changed the content
            if (rawHtml !== `<p>${content}</p>\n` && rawHtml !== content) {
              return DOMPurify.sanitize(rawHtml, {
                ADD_TAGS: ['mark'],
                ADD_ATTR: ['target', 'rel']
              })
            }
          } catch (e) {
            console.warn('Markdown parsing failed, falling back to plain text', e)
          }
        }

        // Fall back to plain text with line breaks
        const escaped = content
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;')
          .replace(/\n/g, '<br>')
        return escaped
      }
    },

    detectHtmlTags(text) {
      if (!text) return false

      // Check for common HTML tags
      const htmlPattern = /<\/?([a-z][a-z0-9]*)\b[^>]*>/i
      return htmlPattern.test(text)
    },
    
    detectMarkdownPatterns(text) {
      if (!text) return false
      
      // Common markdown patterns to detect
      const patterns = [
        /^#{1,6}\s+/m, // Headers
        /\*\*[^*]+\*\*/,  // Bold
        /\*[^*\n]+\*/,    // Italic  
        /\[([^\]]+)\]\(([^)]+)\)/, // Links
        /^[\*\-\+]\s+/m,  // Unordered lists
        /^\d+\.\s+/m,     // Ordered lists
        /^>\s+/m,         // Blockquotes
        /`[^`]+`/,        // Inline code
        /```[\s\S]*?```/, // Code blocks
        /!\[([^\]]*)\]\(([^)]+)\)/, // Images
        /^---+$/m,        // Horizontal rules
        /\~\~[^~]+\~\~/,  // Strikethrough
        /\^\^[^^]+\^\^/,  // Superscript
        /==[^=]+==/       // Highlight
      ]
      
      return patterns.some(pattern => pattern.test(text))
    },
    handleAnswerClick() {
      this.$emit('click', this.answer)
    },
    handleEdit() {
      this.$emit('edit', this.answer)
    },
    async handleFixByAI() {
      // Open dialog and start AI processing
      this.originalAnswerData = JSON.parse(JSON.stringify(this.answer)) // Deep clone
      this.showComparisonDialog = true
      this.isLoadingAI = true
      this.aiResponse = null

      try {
        // Convert answer data to JSON string
        const answerJSON = JSON.stringify(this.answer)

        // The prompt to use for fixing quiz defects
        const systemPrompt = `You are a JSON validator. You will receive a JSON object representing a quiz.

OUTPUT FORMAT - You must return EXACTLY one of these two options:

Option 1: If NO defects found, return exactly:
true

Option 2: If defects found, return the corrected JSON with this EXACT structure:
{"content_type":"quiz","content":"","fileList":[],"quiz_content":{"question":"your question here","options":["option1","option2","option3","option4"],"correct_answer":"B","explanation":"your explanation"},"is_hidden":false}

VALIDATION RULES:
- Check if quiz options are relevant and have a correct answer
- Verify the correct_answer is actually correct
- Ensure explanation is relevant
- Verify key names match: content_type, content, quiz_content, question, options, correct_answer, explanation

CRITICAL RULES:
- Return ONLY raw JSON or the word "true"
- NO markdown (no ###, no **, no formatting)
- NO explanatory text
- NO code blocks (\`\`\`json)
- NO extra fields or comments`

        // Call AI API
        const response = await postRequest('exeModelGeneral', {
          user_id: getUserId(),
          model: 'siliconflow/deepseek-ai/DeepSeek-V3',
          content: answerJSON,
          user_input: systemPrompt,
          source_label: 'Fix Quiz By AI'
        })

        if (response && response.data && response.data.code === 0) {
          this.aiResponse = response.data.data
        } else {
          this.$message.error('Failed to get AI response')
          this.showComparisonDialog = false
        }
      } catch (error) {
        console.error('Error fixing by AI:', error)
        this.$message.error('Failed to fix by AI: ' + error.message)
        this.showComparisonDialog = false
      } finally {
        this.isLoadingAI = false
      }
    },
    confirmAIFix() {
      if (!this.aiResponse) return

      // Check if AI found no defects
      if (this.aiResponse.trim().toLowerCase() === 'true') {
        this.$message.success('No defects found! This card is already correct.')
        this.showComparisonDialog = false
        return
      }

      // Try to parse the revised JSON
      try {
        const revisedAnswer = JSON.parse(this.aiResponse)
        // Emit update event to parent component
        this.$emit('update', revisedAnswer)
        this.$message.success('Card has been fixed by AI!')
        this.showComparisonDialog = false
      } catch (parseError) {
        console.error('Failed to parse AI response:', parseError)
        this.$message.error('Failed to parse AI response. Please try again.')
      }
    },
    cancelAIFix() {
      this.showComparisonDialog = false
      this.aiResponse = null
      this.isLoadingAI = false
      this.originalAnswerData = null
    },
    handleDelete() {
      this.$emit('delete', this.answer)
    },
    isCorrectAnswer(idx) {
      if (!this.answer.quiz_content || !this.answer.quiz_content.correct_answer) {
        return false
      }
      const correctAnswerLetter = this.answer.quiz_content.correct_answer
      const optionLetter = ['A', 'B', 'C', 'D'][idx]
      return correctAnswerLetter === optionLetter
    },
    formatDate(timestamp) {
      // Handle both Unix timestamps (seconds) and JavaScript timestamps (milliseconds)
      const date = timestamp < 10000000000 ? new Date(timestamp * 1000) : new Date(timestamp)
      const now = new Date()
      const diff = now - date
      const seconds = Math.floor(diff / 1000)
      const minutes = Math.floor(seconds / 60)
      const hours = Math.floor(minutes / 60)
      const days = Math.floor(hours / 24)

      if (days > 7) {
        return date.toLocaleDateString()
      } else if (days > 0) {
        return `${days} day${days > 1 ? 's' : ''} ago`
      } else if (hours > 0) {
        return `${hours} hour${hours > 1 ? 's' : ''} ago`
      } else if (minutes > 0) {
        return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
      } else {
        return 'Just now'
      }
    }
  }
}
</script>

<style scoped>
.post-answer-view {
  width: 100%;
  height: auto;
  min-height: 200px;
}

.answer-container {
  width: 100%;
  height: 100%;
  aspect-ratio: 0.8;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease;
  position: relative;
  box-shadow: none !important;
}

:deep(.answer-container.el-card) {
  box-shadow: none !important;
  border: none !important;
}

:deep(.answer-container.el-card:hover) {
  box-shadow: none !important;
  border: none !important;
}

:deep(.answer-container.el-card .el-card__body) {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0;
}

.answer-container:hover {
  transform: translateY(-2px);
}

.answer-content {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  position: relative;
  min-height: 0;
}

.text-content {
  font-size: 14px;
  line-height: 1.6;
  color: #303133;
  word-wrap: break-word;
}

/* Markdown content styling */
.text-content :deep(h1),
.text-content :deep(h2),
.text-content :deep(h3),
.text-content :deep(h4),
.text-content :deep(h5),
.text-content :deep(h6) {
  margin-top: 16px;
  margin-bottom: 12px;
  font-weight: 600;
  line-height: 1.4;
  color: #1a1a1a;
}

.text-content :deep(h1) { font-size: 24px; }
.text-content :deep(h2) { font-size: 20px; }
.text-content :deep(h3) { font-size: 18px; }
.text-content :deep(h4) { font-size: 16px; }
.text-content :deep(h5) { font-size: 14px; }
.text-content :deep(h6) { font-size: 13px; }

.text-content :deep(h1:first-child),
.text-content :deep(h2:first-child),
.text-content :deep(h3:first-child),
.text-content :deep(h4:first-child),
.text-content :deep(h5:first-child),
.text-content :deep(h6:first-child) {
  margin-top: 0;
}

.text-content :deep(p) {
  margin: 0 0 12px 0;
}

.text-content :deep(p:last-child) {
  margin-bottom: 0;
}

.text-content :deep(strong) {
  font-weight: 600;
  color: #1a1a1a;
}

.text-content :deep(em) {
  font-style: italic;
}

.text-content :deep(a) {
  color: #409eff;
  text-decoration: none;
  transition: color 0.2s;
}

.text-content :deep(a:hover) {
  color: #66b1ff;
  text-decoration: underline;
}

.text-content :deep(ul),
.text-content :deep(ol) {
  margin: 12px 0;
  padding-left: 24px;
}

.text-content :deep(ul li),
.text-content :deep(ol li) {
  margin: 6px 0;
  line-height: 1.6;
}

.text-content :deep(blockquote) {
  margin: 12px 0;
  padding: 8px 16px;
  border-left: 4px solid #409eff;
  background-color: #f5f7fa;
  color: #606266;
}

.quote-content {
  margin: 0;
  min-height: 160px;
  padding: 22px 20px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: linear-gradient(180deg, #ffffff 0%, #f7f9fc 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.quote-mark {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 56px;
  line-height: 0.8;
  color: #d6dde8;
  margin-bottom: 8px;
}

.quote-content blockquote {
  margin: 0;
  color: #1d2939;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 20px;
  line-height: 1.45;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.quote-content :deep(p) {
  margin: 0 0 10px;
}

.quote-content :deep(p:last-child) {
  margin-bottom: 0;
}

.quote-content :deep(.quote-placeholder) {
  color: #667085;
  font-family: inherit;
  font-size: 15px;
  font-weight: 400;
}

.text-content :deep(code) {
  padding: 2px 6px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  background-color: #f0f2f5;
  color: #e6428b;
  border-radius: 3px;
}

.text-content :deep(pre) {
  margin: 12px 0;
  padding: 12px;
  background-color: #f5f7fa;
  border-radius: 4px;
  overflow-x: auto;
}

.text-content :deep(pre code) {
  padding: 0;
  background-color: transparent;
  color: #303133;
}

.image-content img {
  width: 100%;
  height: auto;
  max-height: 200px;
  object-fit: cover;
  border-radius: 4px;
}

.video-content video {
  width: 100%;
  height: auto;
  max-height: 200px;
  border-radius: 4px;
}

.quiz-content {
  padding: 10px 0;
}

.quiz-question {
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 15px;
  font-weight: 500;
}

.quiz-option {
  border: 1px solid #DCDFE6;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 8px;
  transition: all 0.3s ease;
  background-color: #FFFFFF;
}

.quiz-option:hover {
  background-color: #F5F7FA;
}

.quiz-option.correct-answer {
  background-color: #f0f9ff;
  border-color: #67c23a;
  border-width: 2px;
}

.quiz-option.correct-answer:hover {
  background-color: #e8f8f5;
}

.quiz-option-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
}

.correct-icon {
  color: #67c23a;
  font-size: 18px;
  font-weight: bold;
  margin-left: 8px;
}

.quiz-explanation {
  margin-top: 15px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 4px;
  border-left: 4px solid #409eff;
}

.explanation-header {
  font-weight: bold;
  color: #409eff;
  margin-bottom: 6px;
  font-size: 12px;
}

.explanation-content {
  font-size: 12px;
  line-height: 1.4;
  color: #606266;
}

.hidden-indicator {
  margin-top: 12px;
  padding: 8px 0;
}

.card-index {
  color: #409EFF;
  font-size: 13px;
  font-weight: 500;
  flex-shrink: 0;
  text-shadow: none !important;
  box-shadow: none !important;
  filter: none !important;
}

.answer-date {
  color: #909399;
  font-size: 12px;
  flex-shrink: 0;
}

.cryptic-tag {
  font-weight: 600;
  font-size: 12px !important;
}

.cryptic-tag i {
  font-weight: bold;
}

.answer-footer {
  padding: 8px 20px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #FAFAFA;
  border-top: 1px solid #EBEEF5;
  margin-top: auto;
  position: relative;
  box-shadow: none !important;
  text-shadow: none !important;
}

.footer-left {
  display: flex;
  align-items: center;
  gap: 10px;
  text-shadow: none !important;
}

.floating-actions {
  position: absolute;
  bottom: 12px;
  right: 12px;
  z-index: 20;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.answer-container:hover .floating-actions {
  opacity: 1;
  pointer-events: auto;
}

.action-menu {
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 4px;
  border: 1px solid #e0e0e0;
}

.action-item {
  padding: 8px 12px;
  font-size: 13px;
  color: #333;
  text-align: left;
  cursor: pointer;
  border: none;
  background: transparent;
  border-radius: 2px;
  transition: background-color 0.15s ease;
  font-family: inherit;
  line-height: 1.4;
  white-space: nowrap;
}

.action-item:hover {
  background-color: #f5f5f5;
}

.action-item:active {
  background-color: #ebebeb;
}

.action-item:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.action-item:disabled:hover {
  background-color: transparent;
}

.action-item.delete-button {
  color: #f56c6c;
}

.action-item.delete-button:hover {
  background-color: #fef0f0;
  color: #f56c6c;
}

.action-item.delete-button:active {
  background-color: #fde2e2;
}

/* Responsive styles */
@media (max-width: 768px) {
  .answer-content {
    padding: 15px;
  }
  
  .answer-footer {
    padding: 8px 15px;
  }
  
  .floating-actions {
    bottom: 8px;
    right: 8px;
  }
  
  .text-content {
    font-size: 13px;
  }
  
  .quiz-question {
    font-size: 13px;
  }
  
  .quiz-option-content {
    font-size: 11px;
  }

  .card-index {
    font-size: 11px;
  }

  .answer-date {
    font-size: 10px;
  }
}

/* Comparison Dialog Styles */
.comparison-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin: 20px 0;
}

.comparison-column {
  display: flex;
  flex-direction: column;
}

.comparison-column h3 {
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  padding-bottom: 12px;
  border-bottom: 2px solid #e0e0e0;
}

.card-preview {
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  min-height: 300px;
  display: flex;
  flex-direction: column;
}

.quiz-display {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.quiz-question-display {
  font-size: 15px;
  line-height: 1.6;
  color: #303133;
}

.quiz-question-display strong {
  color: #409EFF;
  display: block;
  margin-bottom: 8px;
}

.quiz-options-display {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.quiz-option-display {
  padding: 12px 16px;
  background: white;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  transition: all 0.2s;
}

.quiz-option-display.correct {
  background: #f0f9ff;
  border-color: #67c23a;
  border-width: 2px;
}

.quiz-option-display i {
  color: #67c23a;
  font-size: 18px;
}

.quiz-explanation-display {
  padding: 12px;
  background: #fff9e6;
  border-left: 4px solid #409EFF;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.6;
}

.quiz-explanation-display strong {
  color: #409EFF;
  display: block;
  margin-bottom: 6px;
}

.loading-state,
.no-defects-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  text-align: center;
}

.loading-state p,
.error-state p {
  margin-top: 16px;
  color: #606266;
  font-size: 14px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

@media (max-width: 768px) {
  .comparison-container {
    grid-template-columns: 1fr;
  }
}
</style>
